/**
 * Web Frontend Date Formatting Utilities (KST 기준)
 * 
 * 📍 모든 날짜/시간은 KST (Asia/Seoul, UTC+9) 기준입니다
 * - Backend: process.env.TZ = 'Asia/Seoul'
 * - Database: PostgreSQL SET TIMEZONE = 'Asia/Seoul'
 * - Frontend: 이 함수들로 로컬 날짜/시간 처리
 */

/**
 * ⚠️ 로컬 날짜를 YYYY-MM-DD 형식으로 반환 (KST 기준)
 * 
 * 주의: toISOString()은 UTC 기준이므로 사용하면 안 됨!
 * ❌ new Date().toISOString().split('T')[0]  // 자정 근처에 어제 날짜 반환될 수 있음
 * ✅ getLocalDateString()                   // 항상 현재 로컬 날짜 반환
 */
export const getLocalDateString = (date?: Date): string => {
  const d = date || new Date();
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * 날짜를 한국식 포맷으로 표시
 * 예: "2026.02.12"
 */
export const formatDate = (date: string | Date): string => {
  const d = new Date(date);
  return d.toLocaleDateString('ko-KR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
};

/**
 * 날짜와 시간을 한국식 포맷으로 표시
 * 예: "2026.02.12 14:30"
 */
export const formatDateTime = (date: string | Date): string => {
  const d = new Date(date);
  return d.toLocaleString('ko-KR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

/**
 * 시간을 한국식 포맷으로 표시
 * 예: "14:30"
 */
export const formatTime = (date: string | Date): string => {
  const d = new Date(date);
  return d.toLocaleTimeString('ko-KR', {
    hour: '2-digit',
    minute: '2-digit',
  });
};

/**
 * 숫자를 소수점 이하 지정 자리로 포맷
 * 예: 3.14159 → "3.14" (2자리)
 */
export const formatNumber = (num: number, decimals: number = 2): string => {
  return num.toFixed(decimals);
};

/**
 * 금액을 한국 원화로 포맷
 * 예: 10000 → "₩10,000"
 */
export const formatCurrency = (amount: number, currency: string = 'KRW'): string => {
  return new Intl.NumberFormat('ko-KR', {
    style: 'currency',
    currency: currency,
  }).format(amount);
};

/**
 * 걸음수를 목표 달성도와 함께 표시
 * 예: 5000걸음 → "5,000 걸음 (50%)"
 */
export const formatSteps = (steps: number): string => {
  if (steps >= 10000) {
    return '목표 달성! 🎉';
  }
  const percentage = Math.round((steps / 10000) * 100);
  return `${steps.toLocaleString('ko-KR')} 걸음 (${percentage}%)`;
};

/**
 * 체중을 kg 단위로 표시
 * 예: 70 → "70.0 kg"
 */
export const formatWeight = (weight: number): string => {
  return `${weight.toFixed(1)} kg`;
};

/**
 * 혈압을 mmHg 단위로 표시
 * 예: (120, 80) → "120/80 mmHg"
 */
export const formatBloodPressure = (systolic: number, diastolic: number): string => {
  return `${systolic}/${diastolic} mmHg`;
};

/**
 * 텍스트를 지정된 길이로 잘라서 "..."를 붙임
 * 예: "안녕하세요", 3 → "안녕..."
 */
export const truncate = (text: string, length: number): string => {
  return text.length > length ? text.substring(0, length) + '...' : text;
};

/**
 * 텍스트의 첫 글자를 대문자로
 * 예: "hello" → "H"
 */
export const capitalize = (text: string): string => {
  return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
};

/**
 * 텍스트를 제목 형식으로 (각 단어 첫 글자 대문자)
 * 예: "hello world" → "Hello World"
 */
export const toTitleCase = (text: string): string => {
  return text
    .toLowerCase()
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

/**
 * formatters 객체로 모든 함수를 통합 export
 * (shared 패키지와 호환성을 위해)
 */
export const formatters = {
  getLocalDateString,
  formatDate,
  formatDateTime,
  formatTime,
  formatNumber,
  formatCurrency,
  formatSteps,
  formatWeight,
  formatBloodPressure,
  truncate,
  capitalize,
  toTitleCase,
};
